import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import {
  FilterToolbarGroup,
  FilterToolbarItemId,
} from 'src/components/Filters';
import { useInternalNotesFilter } from 'src/components/Filters/InternalNotesFilter';
import { useMergedTagFilters } from 'src/components/MainFilterBar/Tags/useMergedTagFilters';
import { useAppContext } from 'src/contexts/AppContext';
import { useContent } from 'src/contexts/ContentContext';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { DateRangeSelector } from 'src/core/POS/DateRangeSelector';
import { PosEnumMultiSelect } from 'src/core/POS/PosMultiSelect/PosEnumMultiSelect';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Slider } from 'src/core/ui/Slider';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  COLLECTION_FILTER_OP_TO_CID,
  MARKET_PLACE_TO_CID,
  MARKETPLACE_SALE_PAYMENT_STATE_TO_CID,
  SALES_STATUS_TO_CID,
  TICKET_TYPE_TO_CID,
  YES_NO_ENUM_FILTER_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import {
  getDisabledDatesForTimeFrameFilter,
  InhandDateRangePresetNames,
  SaleDateRangeExactPresetNames,
  SaleDateRangeRelativePresetNames,
  StandardDateRangePresetNames,
  StandardDateRangePresetNamesV2,
} from 'src/utils/dateTimeUtils';
import { FromYesNoEnum, ToYesNoEnum } from 'src/utils/eventQueryUtils';
import { formatNumber } from 'src/utils/numberFormatter';
import { ReportTypes } from 'src/utils/reportsUtils';
import {
  ActionOutboxEntityType,
  Feature,
  Marketplace,
  Permission,
  SaleQueryOperations,
  SaleQueryV2,
} from 'src/WebApiController';

import { useDefaultYesNoFilterValues } from '../../Filters/hooks/useDefaultYesNoFilterValues';
import { RowFilter } from '../../Filters/RowFilter';
import { SectionFilter } from '../../Filters/SectionFilter';
import { CurrencyFilterMultiSelector } from '../../Selectors/CurrencyFilterSelector';
import { SellerAccountEmployeeMultiSelector } from '../../Selectors/SellerAccountEmployeeSelector';
import { MAX_NUMBER_OF_TICKETS_HELD } from '../MainFilterBar.constants';
import {
  CommonEventFiltersProps,
  useCommonEventFilters,
} from './useCommonEventFilters';
import { usePurchaseInfoFilters } from './usePurchaseInfoFilters';

export const saleMandatoryFiltersToShow: FilterToolbarItemId[] = [
  'eventDates',
  'saleStatuses',
];

export function useSaleFilters({
  isReportsPage,
  isHorizontalLayout,
  showCatalogFilters,
  showVenueFilter,
  showPerformerFilter,
  firstRenderOpen,
  supportMultiOperation,
}: {
  isReportsPage?: boolean;
  isHorizontalLayout?: boolean;
  showCatalogFilters?: CommonEventFiltersProps<SaleQueryV2>['showCatalogFilters'];
  showVenueFilter?: CommonEventFiltersProps<SaleQueryV2>['showVenueFilter'];
  showPerformerFilter?: CommonEventFiltersProps<SaleQueryV2>['showPerformerFilter'];
  firstRenderOpen?: boolean;
  supportMultiOperation?: boolean;
}) {
  const { loginContext } = useAppContext();

  const hasFilterByCurrencyCodeMultiFeature = useUserHasFeature(
    Feature.FilterByCurrencyCodeMulti
  );
  const hasFilterByCurrencyCodeFeature = !hasFilterByCurrencyCodeMultiFeature;
  const metricsIconNote = useContent(ContentId.ThisFilterWillChangeMetrics);
  const hasAutoFulfillSetting = useUserHasFeature(Feature.AutoFulfillSetting);
  const hasDateFiltersRelativeExactCustomFeature = useUserHasFeature(
    Feature.DateFiltersRelativeExactCustom
  );
  const { shouldEnableEmptySelection } = useDefaultYesNoFilterValues({
    isReportsPage,
  });

  const { initialQuery, tempQuery, setTempQuery } =
    useFilterQueryContext<SaleQueryV2>();

  const purchaseInfoFiltersBase = usePurchaseInfoFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    reportType: isReportsPage ? ReportTypes.Sale : undefined,
    useRelativePresets: isReportsPage,
    supportMultiOperation,
    isHorizontalLayout,
  });

  const tagsFilter = useMergedTagFilters({
    query: tempQuery,
    setQuery: setTempQuery,
    entityType: ActionOutboxEntityType.Sale,
  });

  const purchaseInfoFilters = useMemo(() => {
    return {
      ...purchaseInfoFiltersBase,
      items: [
        ...purchaseInfoFiltersBase.items,
        {
          filterId: 'purchaseVendorOrderId' as FilterToolbarItemId,
          labelContentId: ContentId.ExternalPurchaseId,
          filterQueryKeys: ['purchaseVendorOrderId'] as FilterToolbarItemId[],
          filterItem: (
            <PosTextField
              style={{ width: '100%' }}
              value={tempQuery.purchaseVendorOrderId ?? ''}
              type="text"
              onChange={(e) => {
                const newValue = e.target.value?.length
                  ? e.target.value.trim()
                  : null;
                if (tempQuery.purchaseVendorOrderId !== newValue) {
                  setTempQuery({
                    ...tempQuery,
                    purchaseVendorOrderId: newValue,
                  });
                }
              }}
              maxLength={200}
            />
          ),
        },
      ],
    };
  }, [purchaseInfoFiltersBase, setTempQuery, tempQuery]);

  const notesFilters = useInternalNotesFilter({
    entityType: ActionOutboxEntityType.Sale,
    isHorizontalLayout,
  });

  const seatingFilters = useMemo<FilterToolbarGroup>(
    () => ({
      titleContentId: ContentId.Seating,
      type: 'row',
      items: [
        {
          filterId: 'sectionContains',
          labelContentId: ContentId.Section,
          filterQueryKeys: [
            'sectionEquals',
            'sectionContains',
            'sectionNotContains',
          ],
          filterItem: (
            <SectionFilter
              query={tempQuery}
              setQuery={setTempQuery}
              isHorizontalLayout={isHorizontalLayout}
            />
          ),
        },
        {
          filterId: 'rowContains',
          labelContentId: ContentId.Row,
          filterQueryKeys: ['rowEquals', 'rowContains', 'rowNotContains'],
          filterItem: (
            <RowFilter
              query={tempQuery}
              setQuery={setTempQuery}
              isHorizontalLayout={isHorizontalLayout}
            />
          ),
        },
      ],
    }),
    [isHorizontalLayout, setTempQuery, tempQuery]
  );

  const eventFilters = useCommonEventFilters({
    initialQuery,
    setQuery: setTempQuery,
    query: tempQuery,
    showCatalogFilters,
    showVenueFilter,
    showPerformerFilter,
    firstRenderOpen,
    supportMultiOperation,
    isHorizontalLayout,
    additionalFilters: [
      {
        filterId: 'lastSaleDates' as FilterToolbarItemId,
        labelContentId: ContentId.LastSaleDates,
        filterQueryKeys: ['lastSaleDates'] as FilterToolbarItemId[],
        filterItem: (
          <DateRangeSelector
            useRelativePresets={
              // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
              hasDateFiltersRelativeExactCustomFeature ? false : true
            }
            presetNames={StandardDateRangePresetNames}
            exactPresetNames={
              hasDateFiltersRelativeExactCustomFeature
                ? StandardDateRangePresetNamesV2
                : undefined
            }
            trailingPresetNames={
              hasDateFiltersRelativeExactCustomFeature
                ? StandardDateRangePresetNamesV2
                : undefined
            }
            value={tempQuery.lastSaleDates}
            defaultValue={initialQuery.lastSaleDates}
            onBlur={({ value, operation }) =>
              setTempQuery({
                ...tempQuery,
                lastSaleDates: value,
                operations: supportMultiOperation
                  ? {
                      ...(tempQuery.operations as SaleQueryOperations),
                      lastSaleDatesOp: operation ?? null,
                    }
                  : null,
              })
            }
            disabledDay={(d) =>
              getDisabledDatesForTimeFrameFilter(
                d,
                tempQuery.eventTimeFrameFilter
              )
            }
            operation={tempQuery.operations?.lastSaleDatesOp ?? null}
            enableOperationSelect={supportMultiOperation}
            isHorizontalLayout={isHorizontalLayout}
          />
        ),
      },
      {
        filterId: 'numberOfTicketsHeld' as FilterToolbarItemId,
        labelContentId: ContentId.NumberOfTicketsHeld,
        filterQueryKeys: ['numberOfTicketsHeld'] as FilterToolbarItemId[],
        filterItem: (
          <Slider.Root
            min={0}
            max={MAX_NUMBER_OF_TICKETS_HELD}
            step={1}
            value={[
              tempQuery.numberOfTicketsHeld?.start ?? 0,
              tempQuery.numberOfTicketsHeld?.end ?? MAX_NUMBER_OF_TICKETS_HELD,
            ]}
            renderHandle={(n) => (
              <Slider.Handle>{formatNumber(n)}</Slider.Handle>
            )}
            onValueChange={([start, end]: [number, number]) => {
              if (
                start !== (tempQuery.numberOfTicketsHeld?.start || 0) ||
                end !== tempQuery.numberOfTicketsHeld?.end
              ) {
                const newQuery = {
                  ...tempQuery,
                  numberOfTicketsHeld: {
                    start: start === 0 ? null : start,
                    end: end === MAX_NUMBER_OF_TICKETS_HELD ? null : end,
                  },
                };
                setTempQuery(newQuery);
              }
            }}
          />
        ),
      },
    ],
  });

  return {
    filters: [
      eventFilters,
      {
        titleContentId: ContentId.Fulfillment,
        items: [
          {
            filterId: 'saleStatuses' as FilterToolbarItemId,
            labelContentId: ContentId.Status,
            filterQueryKeys: ['saleStatuses'] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumMultiSelect
                triggerProps={{
                  style: { width: '100%' },
                }}
                values={tempQuery.saleStatuses ?? []}
                placeholderText={ContentId.AllSaleStatuses}
                valueOptionsContent={SALES_STATUS_TO_CID}
                onChange={({ newValues: saleStatuses, operation }) => {
                  if (
                    !isEqual(saleStatuses, tempQuery.saleStatuses) ||
                    operation !== tempQuery.operations?.saleStatusesOp
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      saleStatuses,
                      operations: supportMultiOperation
                        ? {
                            ...(tempQuery.operations as SaleQueryOperations),
                            saleStatusesOp: operation ?? null,
                          }
                        : null,
                    });
                  }
                }}
                operation={tempQuery.operations?.saleStatusesOp ?? null}
                operationOptionsContent={
                  supportMultiOperation
                    ? COLLECTION_FILTER_OP_TO_CID
                    : undefined
                }
                isHorizontalLayout={isHorizontalLayout}
              />
            ),
          },
          {
            filterId: 'inHandDates' as FilterToolbarItemId,
            labelContentId: ContentId.InHand,
            filterQueryKeys: ['inHandDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                useRelativePresets={
                  // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                  hasDateFiltersRelativeExactCustomFeature ? false : true
                }
                exactPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? InhandDateRangePresetNames
                    : undefined
                }
                trailingPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? InhandDateRangePresetNames
                    : undefined
                }
                presetNames={InhandDateRangePresetNames}
                value={tempQuery.inHandDates}
                defaultValue={initialQuery.inHandDates}
                onBlur={({ value, operation }) =>
                  setTempQuery({
                    ...tempQuery,
                    inHandDates: value,
                    operations: supportMultiOperation
                      ? {
                          ...(tempQuery.operations as SaleQueryOperations),
                          inHandDatesOp: operation ?? null,
                        }
                      : null,
                  })
                }
                operation={tempQuery.operations?.inHandDatesOp ?? null}
                enableOperationSelect={supportMultiOperation}
                isHorizontalLayout={isHorizontalLayout}
              />
            ),
          },
          {
            filterId: 'ticketTypes' as FilterToolbarItemId,
            labelContentId: ContentId.TicketType,
            filterQueryKeys: ['ticketTypes'] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumMultiSelect
                style={{ width: '100%' }}
                values={tempQuery.ticketTypes ?? []}
                placeholderText={ContentId.AllTicketTypes}
                enableEmptySelection
                onChange={({ newValues: ticketTypes, operation }) => {
                  if (
                    !isEqual(ticketTypes, tempQuery.ticketTypes) ||
                    operation !== tempQuery.operations?.ticketTypeOp
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      ticketTypes,
                      operations: supportMultiOperation
                        ? {
                            ...(tempQuery.operations as SaleQueryOperations),
                            ticketTypeOp: operation ?? null,
                          }
                        : null,
                    });
                  }
                }}
                valueOptionsContent={TICKET_TYPE_TO_CID}
                operation={tempQuery.operations?.ticketTypeOp ?? null}
                operationOptionsContent={
                  supportMultiOperation
                    ? COLLECTION_FILTER_OP_TO_CID
                    : undefined
                }
                isHorizontalLayout={isHorizontalLayout}
              />
            ),
          },
          {
            filterId: 'fulfillerSellerUserIds' as FilterToolbarItemId,
            labelContentId: ContentId.FulfilledBy,
            filterQueryKeys: [
              'fulfillerSellerUserIds',
            ] as FilterToolbarItemId[],
            filterItem: (
              <SellerAccountEmployeeMultiSelector
                values={tempQuery.fulfillerSellerUserIds ?? []}
                operation={
                  tempQuery.operations?.fulfillerSellerUserIdOp ?? null
                }
                operationOptionsContent={
                  supportMultiOperation
                    ? COLLECTION_FILTER_OP_TO_CID
                    : undefined
                }
                isHorizontalLayout={isHorizontalLayout}
                enableEmptySelection
                style={{ width: '100%' }}
                onChange={({
                  newValues: fulfillerSellerUserIds,
                  operation,
                }) => {
                  if (
                    !isEqual(
                      fulfillerSellerUserIds,
                      tempQuery.fulfillerSellerUserIds
                    ) ||
                    operation !== tempQuery.operations?.fulfillerSellerUserIdOp
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      fulfillerSellerUserIds,
                      operations: supportMultiOperation
                        ? {
                            ...(tempQuery.operations as SaleQueryOperations),
                            fulfillerSellerUserIdOp: operation ?? null,
                          }
                        : null,
                    });
                  }
                }}
              />
            ),
          },
          {
            filterId: 'isSeatSaver' as FilterToolbarItemId,
            labelContentId: ContentId.Placeholder,
            filterQueryKeys: ['isSeatSaver'] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumSelect
                style={{ width: '100%' }}
                value={ToYesNoEnum(tempQuery.isSeatSaver)}
                placeholderText={ContentId.All}
                enableEmptySelection={shouldEnableEmptySelection('isSeatSaver')}
                onChange={(yesNoEnumValue) => {
                  if (yesNoEnumValue !== ToYesNoEnum(tempQuery.isSeatSaver)) {
                    setTempQuery({
                      ...tempQuery,
                      isSeatSaver: FromYesNoEnum(yesNoEnumValue),
                    });
                  }
                }}
                valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
              />
            ),
          },
          {
            filterId: 'fulfillmentDates' as FilterToolbarItemId,
            labelContentId: ContentId.FulfillmentDate,
            filterQueryKeys: ['fulfillmentDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                useRelativePresets={
                  // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                  hasDateFiltersRelativeExactCustomFeature ? false : true
                }
                presetNames={StandardDateRangePresetNames}
                exactPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                trailingPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                value={tempQuery.fulfillmentDates}
                defaultValue={initialQuery.fulfillmentDates}
                onBlur={({ value, operation }) =>
                  setTempQuery({
                    ...tempQuery,
                    fulfillmentDates: value,
                    operations: supportMultiOperation
                      ? {
                          ...(tempQuery.operations as SaleQueryOperations),
                          fulfillmentDatesOp: operation ?? null,
                        }
                      : null,
                  })
                }
                operation={tempQuery.operations?.fulfillmentDatesOp ?? null}
                enableOperationSelect={supportMultiOperation}
                isHorizontalLayout={isHorizontalLayout}
              />
            ),
          },
          ...(hasAutoFulfillSetting
            ? [
                {
                  filterId: 'isNoFulfill' as FilterToolbarItemId,
                  labelContentId: ContentId.AutoFulfill,
                  type: 'row',
                  filterQueryKeys: ['isNoFulfill'] as FilterToolbarItemId[],
                  filterItem: (
                    <PosEnumSelect
                      style={{ width: '100%' }}
                      value={ToYesNoEnum(
                        // the ui is the inverse of isNoFulfil
                        tempQuery.isNoFulfill == null
                          ? null
                          : !tempQuery.isNoFulfill
                      )}
                      placeholderText={ContentId.All}
                      enableEmptySelection={shouldEnableEmptySelection(
                        'isNoFulfill'
                      )}
                      onChange={(yesNoEnumValue) => {
                        if (
                          yesNoEnumValue !==
                          ToYesNoEnum(
                            // the ui is the inverse of isNoFulfil
                            tempQuery.isNoFulfill == null
                              ? null
                              : !tempQuery.isNoFulfill
                          )
                        ) {
                          const v = FromYesNoEnum(yesNoEnumValue);
                          setTempQuery({
                            ...tempQuery,
                            isNoFulfill: v == null ? null : !v, // the ui is the inverse of isNoFulfil
                          });
                        }
                      }}
                      valueOptionsContent={YES_NO_ENUM_FILTER_TO_CID}
                    />
                  ),
                },
              ]
            : []),
          {
            filterId: 'cancellationDates' as FilterToolbarItemId,
            labelContentId: ContentId.CancellationDate,
            filterQueryKeys: ['cancellationDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                useRelativePresets={
                  // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                  hasDateFiltersRelativeExactCustomFeature ? false : true
                }
                presetNames={StandardDateRangePresetNames}
                exactPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                trailingPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                value={tempQuery.cancellationDates}
                defaultValue={initialQuery.cancellationDates}
                onBlur={({ value, operation }) =>
                  setTempQuery({
                    ...tempQuery,
                    cancellationDates: value,
                    operations: supportMultiOperation
                      ? {
                          ...(tempQuery.operations as SaleQueryOperations),
                          cancellationDatesOp: operation ?? null,
                        }
                      : null,
                  })
                }
                operation={tempQuery.operations?.cancellationDatesOp ?? null}
                enableOperationSelect={supportMultiOperation}
                isHorizontalLayout={isHorizontalLayout}
              />
            ),
          },
        ],
      },
      {
        titleContentId: ContentId.WhenAndWhere,
        type: 'row',
        items: [
          {
            filterId: 'soldOnMarketplaces' as FilterToolbarItemId,
            labelContentId: ContentId.Marketplace,
            filterQueryKeys: ['soldOnMarketplaces'] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumMultiSelect
                style={{ width: '100%' }}
                values={tempQuery.soldOnMarketplaces ?? []}
                placeholderText={ContentId.AllMerchants}
                enableEmptySelection
                onChange={({ newValues: soldOnMarketplaces, operation }) => {
                  if (
                    !isEqual(
                      soldOnMarketplaces,
                      tempQuery.soldOnMarketplaces
                    ) ||
                    operation !== tempQuery.operations?.soldOnMarketplaceOp
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      soldOnMarketplaces,
                      operations: supportMultiOperation
                        ? {
                            ...(tempQuery.operations as SaleQueryOperations),
                            soldOnMarketplaceOp: operation ?? null,
                          }
                        : null,
                    });
                  }
                }}
                valueOptionsContent={(
                  loginContext?.user?.activeAccount?.marketplaceSettings?.map(
                    (m) => m.mkp
                  ) ?? [Marketplace.StubHub]
                )
                  .concat(Marketplace.Offline)
                  .concat(Marketplace.Wastage)
                  .reduce(
                    (cur, m) => {
                      cur[m] = MARKET_PLACE_TO_CID[m];
                      return cur;
                    },
                    {} as Record<Marketplace, string>
                  )}
                operation={tempQuery.operations?.soldOnMarketplaceOp ?? null}
                operationOptionsContent={
                  supportMultiOperation
                    ? COLLECTION_FILTER_OP_TO_CID
                    : undefined
                }
                isHorizontalLayout={isHorizontalLayout}
              />
            ),
          },
          {
            filterId: 'saleDates' as FilterToolbarItemId,
            labelContentId: ContentId.SaleDate,
            filterQueryKeys: ['saleDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                presetNames={SaleDateRangeRelativePresetNames}
                exactPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : SaleDateRangeExactPresetNames
                }
                trailingPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : SaleDateRangeRelativePresetNames
                }
                value={tempQuery.saleDates}
                defaultValue={initialQuery.saleDates}
                onBlur={({ value, operation }) => {
                  setTempQuery({
                    ...tempQuery,
                    saleDates: value,
                    operations: supportMultiOperation
                      ? {
                          ...(tempQuery.operations as SaleQueryOperations),
                          saleDatesOp: operation ?? null,
                        }
                      : null,
                  });
                }}
                useRelativePresets={
                  // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                  hasDateFiltersRelativeExactCustomFeature ? false : true
                }
                operation={tempQuery.operations?.saleDatesOp ?? null}
                enableOperationSelect={supportMultiOperation}
                isHorizontalLayout={isHorizontalLayout}
              />
            ),
          },
          {
            filterId: 'listingInsertBys' as FilterToolbarItemId,
            labelContentId: ContentId.OriginatedBy,
            filterQueryKeys: ['listingInsertBys'] as FilterToolbarItemId[],
            filterItem: (
              <SellerAccountEmployeeMultiSelector
                values={tempQuery.listingInsertBys ?? []}
                operation={tempQuery.operations?.listingInsertByOp ?? null}
                operationOptionsContent={
                  supportMultiOperation
                    ? COLLECTION_FILTER_OP_TO_CID
                    : undefined
                }
                isHorizontalLayout={isHorizontalLayout}
                enableEmptySelection
                style={{ width: '100%' }}
                onChange={({ newValues: listingInsertBys, operation }) => {
                  if (
                    !isEqual(listingInsertBys, tempQuery.listingInsertBys) ||
                    operation !== tempQuery.operations?.listingInsertByOp
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      listingInsertBys,
                      operations: supportMultiOperation
                        ? {
                            ...(tempQuery.operations as SaleQueryOperations),
                            listingInsertByOp: operation ?? null,
                          }
                        : null,
                    });
                  }
                }}
              />
            ),
          },
        ],
      },
      {
        titleContentId: ContentId.Payment,
        requiredPermissions: [Permission.Sales_ViewPaymentState],
        type: 'row',
        items: [
          {
            filterId: 'marketplacePaymentStates' as FilterToolbarItemId,
            labelContentId: ContentId.Payment,
            filterQueryKeys: [
              'marketplacePaymentStates',
            ] as FilterToolbarItemId[],
            filterItem: (
              <PosEnumMultiSelect
                style={{ width: '100%' }}
                values={tempQuery.marketplacePaymentStates ?? []}
                placeholderText={ContentId.AllPaymentStates}
                enableEmptySelection
                onChange={({
                  newValues: marketplacePaymentStates,
                  operation,
                }) => {
                  if (
                    !isEqual(
                      marketplacePaymentStates,
                      tempQuery.marketplacePaymentStates
                    ) ||
                    operation !==
                      tempQuery.operations?.marketplacePaymentStateOp
                  ) {
                    setTempQuery({
                      ...tempQuery,
                      marketplacePaymentStates,
                      operations: supportMultiOperation
                        ? {
                            ...(tempQuery.operations as SaleQueryOperations),
                            marketplacePaymentStateOp: operation ?? null,
                          }
                        : null,
                    });
                  }
                }}
                valueOptionsContent={MARKETPLACE_SALE_PAYMENT_STATE_TO_CID}
                operation={
                  tempQuery.operations?.marketplacePaymentStateOp ?? null
                }
                operationOptionsContent={
                  supportMultiOperation
                    ? COLLECTION_FILTER_OP_TO_CID
                    : undefined
                }
                isHorizontalLayout={isHorizontalLayout}
              />
            ),
          },
          {
            filterId: 'paymentDates' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentDate,
            filterQueryKeys: ['paymentDates'] as FilterToolbarItemId[],
            filterItem: (
              <DateRangeSelector
                useRelativePresets={
                  // This is a deprecated prop. Remove once DateFiltersRelativeExactCustom is GA'ed
                  hasDateFiltersRelativeExactCustomFeature ? false : true
                }
                presetNames={StandardDateRangePresetNames}
                exactPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                trailingPresetNames={
                  hasDateFiltersRelativeExactCustomFeature
                    ? StandardDateRangePresetNamesV2
                    : undefined
                }
                value={tempQuery.paymentDates}
                defaultValue={initialQuery.paymentDates}
                onBlur={({ value, operation }) =>
                  setTempQuery({
                    ...tempQuery,
                    paymentDates: value,
                    operations: supportMultiOperation
                      ? {
                          ...(tempQuery.operations as SaleQueryOperations),
                          paymentDatesOp: operation ?? null,
                        }
                      : null,
                  })
                }
                operation={tempQuery.operations?.paymentDatesOp ?? null}
                enableOperationSelect={supportMultiOperation}
                isHorizontalLayout={isHorizontalLayout}
              />
            ),
          },
          {
            filterId: 'paymentReferenceId' as FilterToolbarItemId,
            labelContentId: ContentId.PaymentReference,
            filterQueryKeys: ['paymentReferenceId'] as FilterToolbarItemId[],
            filterItem: (
              <PosTextField
                style={{ width: '50%' }}
                value={tempQuery.paymentReferenceId ?? ''}
                type="text"
                onChange={(e) => {
                  const newValue = e.target.value ?? null;
                  if (tempQuery.paymentReferenceId !== newValue) {
                    setTempQuery({
                      ...tempQuery,
                      paymentReferenceId: newValue,
                    });
                  }
                }}
                maxLength={200}
              />
            ),
          },
          ...(hasFilterByCurrencyCodeMultiFeature ||
          hasFilterByCurrencyCodeFeature
            ? [
                {
                  filterId: 'currencyCodes' as FilterToolbarItemId,
                  labelContentId: ContentId.Currency,
                  filterQueryKeys: ['currencyCodes'] as FilterToolbarItemId[],
                  filterItem: (
                    <CurrencyFilterMultiSelector
                      triggerProps={{ style: { width: '100%' } }}
                      values={tempQuery.currencyCodes ?? []}
                      placeholderText={ContentId.All}
                      onChange={({
                        newValues: newCurrencyCodes,
                        operation,
                      }) => {
                        if (
                          !isEqual(tempQuery.currencyCodes, newCurrencyCodes) ||
                          operation !== tempQuery.operations?.currencyCodesOp
                        ) {
                          setTempQuery({
                            ...tempQuery,
                            currencyCodes: newCurrencyCodes?.length
                              ? newCurrencyCodes
                              : null,
                            operations: supportMultiOperation
                              ? {
                                  ...(tempQuery.operations as SaleQueryOperations),
                                  currencyCodesOp: operation ?? null,
                                }
                              : null,
                          });
                        }
                      }}
                      enableEmptySelection
                      operation={tempQuery.operations?.currencyCodesOp ?? null}
                      operationOptionsContent={
                        supportMultiOperation
                          ? COLLECTION_FILTER_OP_TO_CID
                          : undefined
                      }
                      isHorizontalLayout={isHorizontalLayout}
                    />
                  ),
                },
              ]
            : []),
        ],
      },
      seatingFilters,
      purchaseInfoFilters,
      ...(notesFilters ? [notesFilters] : []),
      ...tagsFilter,
    ],
  };
}
